import React, { useEffect, useRef, useState } from "react";
import Chart from "react-google-charts";

import { getF280DesignHeatingTemp } from "utils/weather";

import Button from "components/Button";
import Dialog, { CloseDialog } from "components/Dialog";
import Select from "components/Input/Select";
import LoadingDots from "components/LoadingDots";
import Tooltip from "components/Tooltip";

import { coreSwitchoverTemperatures, hybridFiveDegreeSegments } from "../eligibility";
import {
    METRICS,
    SENSITIVITY,
    buildTemperatureRows,
    initialSensitivityPrice,
    lowestForMetric,
    metricValue,
    temperatureId,
} from "../resultsModel";
const GRIDLINE_LIGHT = "#eef1f5";
const GRIDLINE_ACTIVE = "#c4cad4";

const vAxisGrid = (title, axisIndex, hoveredAxis) => ({
    title,
    minorGridlines: { count: 0 },
    gridlines: { color: hoveredAxis === axisIndex ? GRIDLINE_ACTIVE : GRIDLINE_LIGHT },
});

import classes from "./styles.module.scss";

const temperatureFinished = (completedTemperatures, temperature) =>
    (completedTemperatures || []).some((value) => Number(value) === Number(temperature));

const temperatureHasResultError = (temperature, completedTemperatures, status, resultsById, activeTemperature) => {
    const finished = temperatureFinished(completedTemperatures, temperature);
    const resultDoc = resultsById?.[temperatureId(temperature)];
    if (finished && !resultDoc) return true;
    if (!finished && status === "complete") return true;
    if (!finished && status === "error" && Number(temperature) === Number(activeTemperature)) return true;
    return false;
};

const nodeState = ({ finished, status, active, resultError }) => {
    if (resultError) return "error";
    if (finished) return "done";
    if (active && status === "running") return "running";
    if (active && status === "error") return "error";
    return "waiting";
};

const nodeTip = (temperature, state, error, resultError) => {
    if (resultError) return `Results not found for switchover point ${temperature}°C.`;
    if (state === "done") return `${temperature}°C switchover run was successful.`;
    if (state === "running") return `EnergyPlus is running the ${temperature}°C switchover simulation.`;
    if (state === "error") return error || `The ${temperature}°C switchover run did not finish.`;
    return `Waiting to run the ${temperature}°C switchover simulation.`;
};

const segmentBounds = (colderIndex, warmerIndex, count) => ({
    left: `calc(100% / ${count} * ${colderIndex} + 100% / ${count * 2})`,
    right: `calc(100% - (100% / ${count} * ${warmerIndex} + 100% / ${count * 2}))`,
});

const interiorSwitchoverTemperatures = (fromC, toC) => {
    const interior = [];
    for (let temp = Number(toC) + 1; temp < Number(fromC); temp += 1) {
        interior.push(temp);
    }
    return interior;
};

const segmentInteriorProgress = (segment, completedTemperatures) => {
    const interior = interiorSwitchoverTemperatures(segment.fromC, segment.toC);
    if (!interior.length) return { completed: 0, total: 0, fillPercent: 0 };
    const completed = interior.filter((temperature) =>
        temperatureFinished(completedTemperatures, temperature)
    ).length;
    return {
        completed,
        total: interior.length,
        fillPercent: (completed / interior.length) * 100,
    };
};

const segmentFailedTemperatures = (
    segment,
    switchoverTemperatures,
    completedTemperatures,
    status,
    temperatureResults,
    activeTemperature
) => {
    if (segment.refinable || segment.refined || segment.refinementRunning) return [];

    const onRun = new Set((switchoverTemperatures || []).map((value) => Number(value)));
    return interiorSwitchoverTemperatures(segment.fromC, segment.toC).filter((temperature) => {
        if (!onRun.has(Number(temperature))) return false;
        return temperatureHasResultError(
            temperature,
            completedTemperatures,
            status,
            temperatureResults,
            activeTemperature
        );
    });
};

const segmentErrorMessage = (
    segment,
    switchoverTemperatures,
    completedTemperatures,
    status,
    temperatureResults,
    activeTemperature
) => {
    const failed = segmentFailedTemperatures(
        segment,
        switchoverTemperatures,
        completedTemperatures,
        status,
        temperatureResults,
        activeTemperature
    );
    return failed.length ? `Results not found for switchover point ${failed[0]}°C.` : "";
};

const RerunConfirmDialog = ({ open, onClose, onConfirm, starting }) => (
    <Dialog open={open} closeActions={onClose}>
        <div className={classes.refinementDialog}>
            <CloseDialog closeActions={onClose} />
            <h2>Re-run analysis?</h2>
            <p className={classes.sectionCopy}>
                This replaces the stored hybrid heating results for this case, including any refined switchover
                temperatures. EnergyPlus will run the full switchover sweep again.
            </p>
            <div className={classes.refinementDialogActions}>
                <Button type="hollow" onClick={onClose} disabled={starting}>
                    Cancel
                </Button>
                <Button onClick={onConfirm} loading={starting}>
                    Re-run analysis
                </Button>
            </div>
        </div>
    </Dialog>
);

const RefinementConfirmDialog = ({ open, range, onClose, onConfirm, refining, refinementError }) =>
    range ? (
        <Dialog open={open} closeActions={onClose}>
            <div className={classes.refinementDialog}>
                <CloseDialog closeActions={onClose} />
                <h2>{range.retry ? "Retry refinement" : "Refine switchover range"}</h2>
                <p className={classes.sectionCopy}>
                    {range.retry
                        ? `Re-queue EnergyPlus simulations between ${range.fromC}°C and ${range.toC}°C for switchover points that did not return results.`
                        : `Add 1°C switchover simulations between ${range.fromC}°C and ${range.toC}°C on this run. New points will appear in the chart and table below once the simulations are complete.`}
                </p>
                <p className={classes.sectionCopy}>
                    This will run {range.missing.length} EnergyPlus simulation
                    {range.missing.length === 1 ? "" : "s"} at {range.missing.map((temp) => `${temp}°C`).join(", ")}.
                </p>
                {refinementError ? <p className={classes.refinementError}>{refinementError}</p> : null}
                <div className={classes.refinementDialogActions}>
                    <Button type="hollow" onClick={onClose} disabled={refining}>
                        Cancel
                    </Button>
                    <Button onClick={onConfirm} loading={refining}>
                        {range.retry ? "Retry refinement" : "Run refinement"}
                    </Button>
                </div>
            </div>
        </Dialog>
    ) : null;

const ProgressLine = ({
    temperatures,
    completedTemperatures,
    status,
    error,
    temperatureResults,
    sweepLocked,
    ready,
    runActive,
    refining,
    onRefinement,
    onRefinementDialogOpen,
    refinementError,
}) => {
    const [dialogRange, setDialogRange] = useState(null);
    const wasRefining = useRef(false);
    const displayTemperatures = coreSwitchoverTemperatures(temperatures);
    const count = displayTemperatures.length || 1;
    const indexOf = (value) => displayTemperatures.findIndex((temp) => Number(temp) === Number(value));
    const activeTemperature = temperatures.find(
        (temperature) => !temperatureFinished(completedTemperatures, temperature)
    );
    const fiveDegreeSegments = hybridFiveDegreeSegments(temperatures, completedTemperatures);
    const anyRefinementRunning = fiveDegreeSegments.some((segment) => segment.refinementRunning);
    const canRefine = sweepLocked && ready && !runActive && !refining;
    const refinementBlocked = runActive || refining || anyRefinementRunning;

    const openRefinement = (segment) => {
        if (!segment.refinable || !canRefine) return;
        onRefinementDialogOpen?.();
        setDialogRange({
            fromC: segment.fromC,
            toC: segment.toC,
            missing: segment.missing,
            retry: false,
        });
    };

    const openRefinementRetry = (segment, failedTemperatures) => {
        if (!failedTemperatures.length || runActive || refining) return;
        onRefinementDialogOpen?.();
        setDialogRange({
            fromC: segment.fromC,
            toC: segment.toC,
            missing: failedTemperatures,
            retry: true,
        });
    };

    const confirmRefinement = () => {
        if (!dialogRange || !onRefinement) return;
        onRefinement({ fromC: dialogRange.fromC, toC: dialogRange.toC, retry: Boolean(dialogRange.retry) });
    };

    const closeDialog = () => {
        if (refining) return;
        setDialogRange(null);
    };

    useEffect(() => {
        if (wasRefining.current && !refining && dialogRange && !refinementError) {
            setDialogRange(null);
        }
        wasRefining.current = refining;
    }, [refining, refinementError, dialogRange]);

    return (
        <>
            <div className={classes.track} style={{ gridTemplateColumns: `repeat(${count}, 1fr)` }}>
                <div className={classes.rail} />
                {fiveDegreeSegments.map((segment) => {
                    const colderIndex = indexOf(segment.toC);
                    const warmerIndex = indexOf(segment.fromC);
                    if (colderIndex < 0 || warmerIndex < 0) return null;

                    const bounds = segmentBounds(colderIndex, warmerIndex, count);
                    const segmentKey = `${segment.fromC}-${segment.toC}`;
                    const tipId = `hybrid-segment-${segmentKey}`;

                    const failedTemperatures = segmentFailedTemperatures(
                        segment,
                        temperatures,
                        completedTemperatures,
                        status,
                        temperatureResults,
                        activeTemperature
                    );
                    const segmentError = failedTemperatures.length
                        ? `Results not found for switchover point ${failedTemperatures[0]}°C.`
                        : "";
                    const canRetrySegment =
                        Boolean(segmentError) &&
                        failedTemperatures.length > 0 &&
                        ready &&
                        !runActive &&
                        !refining &&
                        !anyRefinementRunning;

                    if (segment.refined) {
                        return (
                            <div
                                key={segmentKey}
                                className={`${classes.segmentBand} ${classes.segmentRefined}`}
                                style={bounds}
                                aria-hidden
                            />
                        );
                    }

                    if (segment.refinementRunning) {
                        const { completed, total, fillPercent } = segmentInteriorProgress(
                            segment,
                            completedTemperatures
                        );
                        const progressTip =
                            total > 0
                                ? `Refinement in progress (${completed} of ${total} complete).`
                                : "Refinement simulations in progress.";
                        return (
                            <div
                                key={segmentKey}
                                className={classes.segmentStatusWrap}
                                style={bounds}
                                data-tip={progressTip}
                                data-for={tipId}
                            >
                                <div className={classes.segmentProgressTrack} aria-hidden>
                                    <div
                                        className={classes.segmentProgressFill}
                                        style={{ width: `${fillPercent}%` }}
                                    />
                                    {fillPercent < 100 ? (
                                        <div
                                            className={classes.segmentRunningChevrons}
                                            style={{ left: `${fillPercent}%` }}
                                        />
                                    ) : null}
                                </div>
                                <Tooltip id={tipId} />
                            </div>
                        );
                    }

                    if (segmentError) {
                        const retryTip = canRetrySegment
                            ? `${segmentError} Click to retry this refinement.`
                            : segmentError;
                        return (
                            <div key={segmentKey} className={classes.segmentInteractive} style={bounds}>
                                <div className={`${classes.segmentBand} ${classes.segmentError}`} aria-hidden />
                                <button
                                    type="button"
                                    className={classes.segmentHit}
                                    aria-label={retryTip}
                                    data-tip={retryTip}
                                    data-for={tipId}
                                    disabled={!canRetrySegment}
                                    onClick={() => openRefinementRetry(segment, failedTemperatures)}
                                />
                                <Tooltip id={tipId} />
                            </div>
                        );
                    }

                    if (segment.refinable) {
                        const segmentTip = canRefine
                            ? "Click to run additional points"
                            : refinementBlocked
                              ? "Wait for the current simulations to finish before refining this span."
                              : "Complete both switchover points before refining this span.";
                        return (
                            <div key={segmentKey} className={classes.segmentInteractive} style={bounds}>
                                <div className={`${classes.segmentBand} ${classes.segmentGrey}`} aria-hidden />
                                <button
                                    type="button"
                                    className={classes.segmentHit}
                                    aria-label={`Refine ${segment.fromC}°C to ${segment.toC}°C`}
                                    data-tip={segmentTip}
                                    data-for={tipId}
                                    disabled={!canRefine}
                                    onClick={() => openRefinement(segment)}
                                />
                                <Tooltip id={tipId} />
                            </div>
                        );
                    }

                    return (
                        <div
                            key={segmentKey}
                            className={`${classes.segmentBand} ${classes.segmentGrey}`}
                            style={bounds}
                            aria-hidden
                        />
                    );
                })}
                {displayTemperatures.map((temperature) => {
                    const resultError = temperatureHasResultError(
                        temperature,
                        completedTemperatures,
                        status,
                        temperatureResults,
                        activeTemperature
                    );
                    const state = nodeState({
                        finished: temperatureFinished(completedTemperatures, temperature),
                        status,
                        active: Number(temperature) === Number(activeTemperature),
                        resultError,
                    });
                    const tipId = `hybrid-switchover-${temperature}`;
                    return (
                        <div key={temperature} className={classes.nodeWrap}>
                            <button
                                type="button"
                                className={`${classes.dot} ${classes[state]}`}
                                aria-label={nodeTip(temperature, state, error, resultError)}
                                data-tip={nodeTip(temperature, state, error, resultError)}
                                data-for={tipId}
                            />
                            <Tooltip id={tipId} />
                            <span className={classes.nodeLabel}>{temperature}°</span>
                        </div>
                    );
                })}
            </div>
            <RefinementConfirmDialog
                open={Boolean(dialogRange)}
                range={dialogRange}
                onClose={closeDialog}
                onConfirm={confirmRefinement}
                refining={refining}
                refinementError={refinementError}
            />
        </>
    );
};

const money = (value) =>
    Number.isFinite(value)
        ? value.toLocaleString("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 })
        : "—";

const formatMetric = (metric, value, backupUnit) => {
    if (!Number.isFinite(value)) return "—";
    if (metric.money) return money(value);
    const unit = metric.value === "backupQuantity" ? backupUnit : metric.unit;
    return `${value.toLocaleString("en-CA", { maximumFractionDigits: metric.digits ?? 0 })} ${unit}`;
};

/** Non-currency units live in the table header; cells show numbers or $ only. */
const metricTableHeaderUnit = (metric, backupUnit) => {
    if (metric.value === "backupQuantity") return backupUnit || "";
    if (metric.money) return (metric.unit || "").replace(/^\$/, "");
    return metric.unit || "";
};

const tableColumnHeader = (metric, backupUnit) => {
    const unit = metricTableHeaderUnit(metric, backupUnit);
    return unit ? `${metric.label} (${unit})` : metric.label;
};

const formatTableCell = (metric, value) => {
    if (!Number.isFinite(value)) return "—";
    if (metric.money) return money(value);
    const digits = metric.digits ?? 0;
    return value.toLocaleString("en-CA", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
};

const metricRangeLabel = (metric, rows) => {
    const values = rows.map((row) => metricValue(row, metric.value)).filter((value) => Number.isFinite(value));
    if (!values.length) return "—";
    const min = Math.min(...values);
    const max = Math.max(...values);
    if (min === max) return formatTableCell(metric, min);
    return `${formatTableCell(metric, min)} – ${formatTableCell(metric, max)}`;
};

const metricAxisTitle = (metricValueKey, backupUnit) => {
    const metric = METRICS.find(({ value }) => value === metricValueKey);
    if (!metric) return "";
    const unit = metric.value === "backupQuantity" && backupUnit ? backupUnit : metric.unit;
    return unit ? `${metric.label} (${unit})` : metric.label;
};

const SENSITIVITY_POINT_BLUE = "#4285f4";
const SENSITIVITY_POINT_ORANGE = "#e39b2b";

const chartRows = (rows, leftMetric, rightMetric) => {
    const left = METRICS.find(({ value }) => value === leftMetric);
    const right = METRICS.find(({ value }) => value === rightMetric);
    const header = ["Switchover (°C)", left?.label || "Metric"];
    if (right) header.push(right.label);
    return [
        header,
        ...rows.map((row) => {
            const point = [row.temperature, metricValue(row, leftMetric)];
            if (right) point.push(metricValue(row, rightMetric));
            return point;
        }),
    ];
};

const sensitivityChartRows = (rows, lowestRow) => {
    const costLabel = METRICS.find(({ value }) => value === "operatingCost")?.label || "Operating cost";
    return [
        ["Switchover (°C)", costLabel, { role: "style", type: "string" }],
        ...rows.map((row) => {
            const isLowest = lowestRow && Number(row.temperature) === Number(lowestRow.temperature);
            const fill = isLowest ? SENSITIVITY_POINT_ORANGE : SENSITIVITY_POINT_BLUE;
            return [
                row.temperature,
                metricValue(row, "operatingCost"),
                `point { fill-color: ${fill}; stroke-color: ${fill} }`,
            ];
        }),
    ];
};

const Comparison = ({ rows, backupUnit }) => {
    const [leftMetric, setLeftMetric] = useState("operatingCost");
    const [rightMetric, setRightMetric] = useState("emissionsTonnes");
    const [tableHighlightMetric, setTableHighlightMetric] = useState("operatingCost");
    const [hoveredAxis, setHoveredAxis] = useState(null);
    const highlighted = lowestForMetric(rows, tableHighlightMetric);
    const highlightMetricDef = METRICS.find(({ value }) => value === tableHighlightMetric);
    const metricOptions = METRICS.filter((metric) => metric.value !== "backupQuantity" || backupUnit).map((metric) => ({
        value: metric.value,
        label:
            metric.value === "backupQuantity" && backupUnit
                ? `Backup fuel (${backupUnit})`
                : metric.unit
                  ? `${metric.label} (${metric.unit})`
                  : metric.label,
    }));

    return (
        <div className={classes.comparison}>
            {highlighted && highlightMetricDef ? (
                <p className={classes.sectionCopy}>
                    Lowest {highlightMetricDef.label.toLowerCase()} is at {highlighted.temperature}°C (
                    {formatMetric(highlightMetricDef, metricValue(highlighted, tableHighlightMetric), backupUnit)}).
                    Click a segment between points to run 1°C increments for that range.
                </p>
            ) : null}
            <div className={classes.metricRow}>
                <Select
                    name="hybridLeftMetric"
                    label="Left axis"
                    options={metricOptions}
                    input={{ value: leftMetric, onChange: setLeftMetric }}
                />
                <Select
                    name="hybridRightMetric"
                    label="Right axis"
                    options={[{ value: "", label: "None" }, ...metricOptions]}
                    input={{ value: rightMetric, onChange: setRightMetric }}
                />
            </div>
            <div className={classes.chartWithAxes}>
                <div
                    className={classes.chartAxisHitLeft}
                    onMouseEnter={() => setHoveredAxis(0)}
                    onMouseLeave={() => setHoveredAxis(null)}
                    aria-hidden
                />
                {rightMetric ? (
                    <div
                        className={classes.chartAxisHitRight}
                        onMouseEnter={() => setHoveredAxis(1)}
                        onMouseLeave={() => setHoveredAxis(null)}
                        aria-hidden
                    />
                ) : null}
                <Chart
                    chartType="ScatterChart"
                    width="100%"
                    height="372px"
                    loader={<LoadingDots />}
                    data={chartRows(rows, leftMetric, rightMetric)}
                    options={{
                        legend: { position: "bottom" },
                        pointSize: 8,
                        chartArea: { left: 96, right: rightMetric ? 96 : 32, top: 24, bottom: 72 },
                        hAxis: { title: "Switchover temperature (°C)\n" },
                        vAxis: rightMetric
                            ? undefined
                            : vAxisGrid(metricAxisTitle(leftMetric, backupUnit), 0, hoveredAxis),
                        series: rightMetric ? { 1: { targetAxisIndex: 1 } } : undefined,
                        vAxes: rightMetric
                            ? {
                                  0: vAxisGrid(metricAxisTitle(leftMetric, backupUnit), 0, hoveredAxis),
                                  1: vAxisGrid(metricAxisTitle(rightMetric, backupUnit), 1, hoveredAxis),
                              }
                            : undefined,
                    }}
                />
            </div>
            <div className={classes.tableWrap}>
                <table className={classes.table}>
                    <thead>
                        <tr>
                            <th>Switchover (°C)</th>
                            {METRICS.map((metric) => {
                                if (metric.value === "backupQuantity" && !backupUnit) return null;
                                const isActive = tableHighlightMetric === metric.value;
                                return (
                                    <th key={metric.value} className={isActive ? classes.tableHeaderActive : ""}>
                                        <button
                                            type="button"
                                            className={classes.tableHeaderButton}
                                            onClick={() => setTableHighlightMetric(metric.value)}
                                            aria-pressed={isActive}
                                        >
                                            {tableColumnHeader(metric, backupUnit)}
                                        </button>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr
                                key={row.temperature}
                                className={
                                    highlighted && row.temperature === highlighted.temperature ? classes.lowest : ""
                                }
                            >
                                <td>{row.temperature}</td>
                                {METRICS.map((metric) => {
                                    if (metric.value === "backupQuantity" && !backupUnit) return null;
                                    return (
                                        <td key={metric.value}>
                                            {formatTableCell(metric, metricValue(row, metric.value))}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr className={classes.tableRange}>
                            <td>Range</td>
                            {METRICS.map((metric) => {
                                if (metric.value === "backupQuantity" && !backupUnit) return null;
                                return <td key={metric.value}>{metricRangeLabel(metric, rows)}</td>;
                            })}
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
};

const Sensitivity = ({ temperatures, temperatureResults, analysis, backupFuelKey }) => {
    const [hoveredAxis, setHoveredAxis] = useState(null);
    const electricityRange = SENSITIVITY.electricity;
    const backupRange = SENSITIVITY[backupFuelKey];
    const hasTouCosts = analysis?.electricityRateType === "timeOfUse";
    const [electricityRateMode, setElectricityRateMode] = useState("flat");
    const currentElectricity = Math.round(Number(analysis?.fuelPrices?.electricity?.dollarsPerUnit || 0) * 100);
    const [electricityCents, setElectricityCents] = useState(
        Math.min(electricityRange.max, Math.max(electricityRange.min, currentElectricity || electricityRange.min))
    );
    const currentBackup = initialSensitivityPrice(backupFuelKey, analysis?.fuelPrices);
    const [backupPrice, setBackupPrice] = useState(currentBackup ?? backupRange?.min ?? 0);
    const useTouElectricity = hasTouCosts && electricityRateMode === "timeOfUse";

    if (!backupRange) return null;

    const rows = buildTemperatureRows({
        resultsById: temperatureResults,
        temperatures,
        fuelPrices: analysis?.fuelPrices,
        timeOfUse: analysis?.timeOfUse,
        electricityRateType: useTouElectricity ? "timeOfUse" : "flat",
        capitalCosts: analysis?.capitalCosts,
        lifetimeYears: analysis?.lifetimeYears,
        backupFuelKey,
        priceOverrides: useTouElectricity
            ? { backupCentsPerUnit: backupPrice }
            : {
                  electricityDollarsPerKwh: electricityCents / 100,
                  backupCentsPerUnit: backupPrice,
              },
    });
    const lowest = lowestForMetric(rows, "operatingCost");

    return (
        <div className={classes.sensitivity}>
            <h3>Price sensitivity</h3>
            <p className={classes.sectionCopy}>
                See how the cost-optimal switchover temperature changes with different electricity and backup fuel
                prices.
                <br />
                Effective electricity price range is {electricityRange.min}–{electricityRange.max}{" "}
                {electricityRange.unit}. Backup fuel effective price range is {backupRange.min}–{backupRange.max}{" "}
                {backupRange.unit}.
            </p>
            <div className={classes.sensitivityRateRow}>
                <label className={classes.slider}>
                    Electricity{" "}
                    {useTouElectricity
                        ? "(Costs tab TOU schedule)"
                        : `(${electricityCents}${electricityRange.unit})`}
                    <input
                        type="range"
                        min={electricityRange.min}
                        max={electricityRange.max}
                        step={electricityRange.step}
                        value={electricityCents}
                        disabled={useTouElectricity}
                        onChange={(event) => setElectricityCents(Number(event.target.value))}
                    />
                </label>
                {hasTouCosts ? (
                    <div className={classes.rateToggle} role="group" aria-label="Electricity rate for sensitivity">
                        <button
                            type="button"
                            className={electricityRateMode === "flat" ? classes.rateToggleActive : ""}
                            onClick={() => setElectricityRateMode("flat")}
                        >
                            Flat
                        </button>
                        <button
                            type="button"
                            className={electricityRateMode === "timeOfUse" ? classes.rateToggleActive : ""}
                            onClick={() => setElectricityRateMode("timeOfUse")}
                        >
                            TOU
                        </button>
                    </div>
                ) : null}
            </div>
            <label className={classes.slider}>
                Backup fuel ({backupPrice}
                {backupRange.unit})
                <input
                    type="range"
                    min={backupRange.min}
                    max={backupRange.max}
                    step={backupRange.step}
                    value={backupPrice}
                    onChange={(event) => setBackupPrice(Number(event.target.value))}
                />
            </label>
            {lowest ? (
                <p className={classes.sectionCopy}>
                    At these prices, the lowest operating cost is at {lowest.temperature}°C (
                    {money(lowest.operatingCost)} per year). The orange point marks the lowest cost; blue points are
                    other switchover temperatures.
                </p>
            ) : null}
            <div className={classes.chartWithAxes}>
                <div
                    className={classes.chartAxisHitLeft}
                    onMouseEnter={() => setHoveredAxis(0)}
                    onMouseLeave={() => setHoveredAxis(null)}
                    aria-hidden
                />
                <Chart
                    chartType="ScatterChart"
                    width="100%"
                    height="288px"
                    loader={<LoadingDots />}
                    data={sensitivityChartRows(rows, lowest)}
                    options={{
                        legend: { position: "none" },
                        pointSize: 8,
                        colors: [SENSITIVITY_POINT_BLUE],
                        chartArea: { left: 96, right: 24, top: 16, bottom: 57 },
                        hAxis: { title: "Switchover temperature (°C)" },
                        vAxis: vAxisGrid("Operating cost ($/yr)", 0, hoveredAxis),
                    }}
                />
            </div>
        </div>
    );
};

const Results = ({
    statusMessage,
    ready,
    canRun,
    canStartSweep,
    sweepLocked,
    replaceWarning,
    runActive,
    starting,
    refining,
    refinementError,
    onRun,
    onRefinement,
    onRefinementDialogOpen,
    packageChanged,
    showProgress,
    run,
    temperatures,
    temperatureResults,
    hybridAnalysis,
    packageResults,
    modelData,
}) => {
    const backupFuelKey = run?.backupFuelKey || "";
    const packageId = run?.upgradePackageId || "base";
    const resultsCaseKey = packageId === "base" ? "generalBaseCase" : "generalAllUpgrades";
    const caseResults = packageResults?.[packageId]?.resultsData?.[resultsCaseKey] || {};
    const heatLossWatts = caseResults?.f280Results?.heatLoss;
    const designTempC = getF280DesignHeatingTemp(modelData?.location?.weather?.f280?.location?.id || 0);
    const rows = buildTemperatureRows({
        resultsById: temperatureResults || {},
        temperatures,
        fuelPrices: hybridAnalysis?.fuelPrices,
        timeOfUse: hybridAnalysis?.timeOfUse,
        electricityRateType: hybridAnalysis?.electricityRateType,
        capitalCosts: hybridAnalysis?.capitalCosts,
        lifetimeYears: hybridAnalysis?.lifetimeYears,
        heatLossWatts,
        designTempC,
        backupFuelKey,
        regionId: modelData?.location?.weather?.region?.id,
    });
    const backupUnit = rows.find((row) => row.backupUnit)?.backupUnit || "";
    const [rerunConfirmOpen, setRerunConfirmOpen] = useState(false);

    const confirmRerun = () => {
        onRun({ replaceSamePackage: true });
    };

    useEffect(() => {
        if (!starting) setRerunConfirmOpen(false);
    }, [starting]);

    return (
        <div className={classes.panel}>
            <p className={classes.runNote}>{statusMessage}</p>
            <div className={classes.resultsActions}>
                <Button
                    onClick={() => onRun()}
                    disabled={!ready || !canStartSweep}
                    loading={(starting && !sweepLocked) || runActive}
                >
                    Run analysis
                </Button>
                {sweepLocked ? (
                    <Button
                        type="hollow"
                        onClick={() => setRerunConfirmOpen(true)}
                        disabled={!ready || !canRun || runActive}
                        loading={starting}
                    >
                        Re-run analysis
                    </Button>
                ) : null}
            </div>
            <RerunConfirmDialog
                open={rerunConfirmOpen}
                onClose={() => {
                    if (!starting) setRerunConfirmOpen(false);
                }}
                onConfirm={confirmRerun}
                starting={starting}
            />
            {replaceWarning ? <p className={classes.replaceWarning}>{replaceWarning}</p> : null}
            {sweepLocked ? (
                <p className={classes.replaceWarning}>
                    Changing the model does not update these results. Re-run when the house has changed enough to need a
                    new analysis. Re-running replaces the stored results.
                </p>
            ) : null}
            {showProgress ? (
                <ProgressLine
                    temperatures={temperatures}
                    completedTemperatures={run.completedTemperatures}
                    status={run.status}
                    error={run.error}
                    temperatureResults={temperatureResults}
                    sweepLocked={sweepLocked}
                    ready={ready}
                    runActive={runActive}
                    refining={refining}
                    onRefinement={onRefinement}
                    onRefinementDialogOpen={onRefinementDialogOpen}
                    refinementError={refinementError}
                />
            ) : (
                <p className={classes.empty}>
                    An analysis runs EnergyPlus simulations across the switchover temperatures on this run. Cost metrics
                    update when the prices on the Costs tab change.
                </p>
            )}
            {temperatureResults == null ? (
                <p className={classes.empty}>Stored results could not be read.</p>
            ) : rows.length ? (
                <>
                    <Comparison rows={rows} backupUnit={backupUnit} />
                    <Sensitivity
                        temperatures={temperatures}
                        temperatureResults={temperatureResults}
                        analysis={hybridAnalysis}
                        backupFuelKey={backupFuelKey}
                    />
                </>
            ) : null}
        </div>
    );
};

export default Results;
